import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Building2, ExternalLink, Loader2, Smartphone, Globe } from "lucide-react";
import { parsePublicQrInput } from "@mvk/shared";
import { apiRequest } from "../api/client";
import { visitorAuthService } from "../services/visitorAuthService";
import { maxPlatform } from "../services/maxPlatform";
import { useAppStore } from "../store/useAppStore";
import { PENDING_QR_KEY, claimPendingQr } from "../services/publicQrFlow";
import { useTranslation } from "react-i18next";

type Preview = {
  kind: string;
  opaque: string;
  eventId: string | null;
  title: string;
  subtitle: string | null;
  companyId: string | null;
  maxDeepLink: string;
  browserPath: string;
};

function pathFromParams(
  params: Record<string, string | undefined>,
  search: string,
): string {
  if (params.eventId && params.booth && !params.companyId) {
    return `/go/booth-slot/${params.eventId}/${params.booth}`;
  }
  if (params.eventId && params.companyId) {
    return `/go/booth/${params.eventId}/${params.companyId}`;
  }
  if (params.companyId && !params.eventId) {
    return `/go/company/${params.companyId}`;
  }
  if (params.repId) return `/go/badge/${params.repId}`;
  if (params.productId) return `/go/product/${params.productId}`;
  if (params.code) return `/go/c/${params.code}`;
  const q = new URLSearchParams(search).get("code");
  if (q) return q;
  return "";
}

export function QrGatewayPage() {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectEvent, completeOnboarding } = useAppStore();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const rawPath = pathFromParams(params as Record<string, string | undefined>, location.search);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        if (!rawPath) {
          setError(t("visitor.invalid_qr"));
          return;
        }
        const res = await apiRequest<{ preview: Preview }>(
          `/public/qr/preview?code=${encodeURIComponent(rawPath)}`,
          { public: true },
        );
        if (cancelled) return;
        setPreview(res.preview);
        sessionStorage.setItem(PENDING_QR_KEY, res.preview.opaque);

        // Already inside mini-app / logged-in browser → claim immediately
        if (visitorAuthService.isLoggedIn()) {
          setBusy(true);
          const claimed = await claimPendingQr();
          if (claimed?.eventId) {
            selectEvent(claimed.eventId);
            completeOnboarding();
          }
          navigate(claimed?.navigateTo ?? "/home", { replace: true });
          return;
        }
        if (maxPlatform.isAvailable()) {
          setBusy(true);
          const ok = await visitorAuthService.tryMaxBootstrap(
            () => undefined,
            res.preview.opaque,
          );
          if (ok) {
            const claimed = await claimPendingQr();
            if (claimed?.eventId) {
              selectEvent(claimed.eventId);
              completeOnboarding();
            }
            navigate(claimed?.navigateTo ?? "/home", { replace: true });
            return;
          }
          setBusy(false);
        }
      } catch {
        if (!cancelled) setError(t("visitor.qr_recognition_failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rawPath, navigate, selectEvent, completeOnboarding, t]);

  const openMax = () => {
    if (!preview) return;
    sessionStorage.setItem(PENDING_QR_KEY, preview.opaque);
    maxPlatform.openExternalLink(preview.maxDeepLink);
  };

  const openBrowser = () => {
    if (!preview) return;
    sessionStorage.setItem(PENDING_QR_KEY, preview.opaque);
    navigate(`/login?next=${encodeURIComponent(preview.browserPath)}`, { replace: true });
  };

  if (loading || busy) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-3 px-6 text-gray-500">
        <Loader2 className="animate-spin" size={28} />
        <p className="text-[14px]">{t("visitor.opening")}</p>
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-[15px] text-gray-700">{error || t("visitor.qr_not_found")}</p>
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="text-[13px] text-indigo-600"
        >
          {t("visitor.go_to_login")}
        </button>
      </div>
    );
  }

  const parsed = parsePublicQrInput(preview.opaque);

  return (
    <div className="min-h-[100dvh] flex flex-col px-5 pt-10 pb-8 max-w-lg mx-auto">
      <div className="flex-1 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
          <Building2 size={28} className="text-indigo-600" />
        </div>
        <p className="text-[12px] uppercase tracking-wide text-indigo-500 mb-1">
          {parsed?.kind === "booth"
            ? t("visitor.booth")
            : parsed?.kind === "badge"
              ? t("visitor.representative")
              : parsed?.kind === "product"
                ? t("visitor.product")
                : t("companies.detail_title")}
        </p>
        <h1 className="text-[22px] text-gray-900 mb-1">{preview.title}</h1>
        {preview.subtitle && (
          <p className="text-[14px] text-gray-500 mb-6">{preview.subtitle}</p>
        )}
        <p className="text-[14px] text-gray-600 leading-relaxed max-w-sm mb-8">
          {t("visitor.scanned_outside")}
        </p>

        <div className="w-full space-y-3">
          <button
            type="button"
            onClick={openMax}
            className="w-full flex items-center gap-3 px-4 py-4 rounded-2xl bg-indigo-600 text-white text-left active:bg-indigo-700"
          >
            <Smartphone size={22} className="flex-shrink-0" />
            <span className="flex-1">
              <span className="block text-[15px]">{t("visitor.open_max")}</span>
              <span className="block text-[12px] text-indigo-100 mt-0.5">
                {t("visitor.max_fast_login")}
              </span>
            </span>
            <ExternalLink size={16} className="opacity-70" />
          </button>

          <button
            type="button"
            onClick={openBrowser}
            className="w-full flex items-center gap-3 px-4 py-4 rounded-2xl bg-white border border-gray-200 text-left active:bg-gray-50"
          >
            <Globe size={22} className="flex-shrink-0 text-gray-700" />
            <span className="flex-1">
              <span className="block text-[15px] text-gray-900">{t("visitor.open_browser")}</span>
              <span className="block text-[12px] text-gray-500 mt-0.5">
                {t("visitor.browser_login_hint")}
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
